import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, X, Edit3, Archive, ArchiveRestore, Power, Gift } from "lucide-react";
import api from "@/lib/api";
import Pager from "@/components/Pager";

const empty = {
  title: "",
  body: "",
  direction: "BUY",
  symbol: "",
  entry: "",
  sl: "",
  tp: "",
  tags: [],
  status: "published",
};

export default function AdminSignals() {
  const [tab, setTab] = useState("posts");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [tg, setTg] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);
  const [detected, setDetected] = useState(null);
  const loadTg = () => api.get("/admin/telegram/readiness").then((r) => setTg(r.data)).catch(() => {});
  useEffect(() => { if (tab === "telegram") loadTg(); /* eslint-disable-next-line */ }, [tab]);
  const setupWebhook = async () => {
    setTgBusy(true);
    try { await api.post("/admin/telegram/setup-webhook"); toast.success("Telegram webhook registered"); loadTg(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };
  const runSweep = async () => {
    setTgBusy(true);
    try { const { data } = await api.post("/admin/telegram/run-sweep"); toast.success(`Sweep done — removed ${data.expired_removed} expired`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };
  const runReminders = async () => {
    setTgBusy(true);
    try { const { data } = await api.post("/admin/subscriptions/run-reminders"); toast.success(`Renewal reminders sent: ${data.reminders_sent}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };
  const testTg = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/test");
      if (data.message_sent) toast.success(`Connected: @${data.bot_username} → ${data.chat_title || "channel"} — test message sent`);
      else if (data.error) toast.error(data.error);
      else toast.warning(`Bot @${data.bot_username} is OK, but the channel isn't connected. ${data.hint || "Set the numeric channel id."}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Not connected"); } finally { setTgBusy(false); }
  };
  const getInvite = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/invite");
      try { await navigator.clipboard?.writeText(data.invite_link); } catch { /* ignore */ }
      toast.success("Admin invite link copied to clipboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };
  const detectChats = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/detect-chats");
      setDetected(data);
      if (data.webhook_active) toast.warning(data.note);
      else if (!data.chats.length) toast.warning(data.note || "No groups detected yet");
      else toast.success(`Found ${data.chats.length} chat(s) — click one to save it`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };
  const saveChannelId = async (id) => {
    try {
      await api.put("/admin/api-keys/telegram_channel_id", { value: String(id) });
      toast.success(`Saved channel id ${id}`);
      setDetected(null); loadTg();
    } catch { toast.error("Failed to save"); }
  };
  const setupMenu = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/setup-menu");
      toast.success(`Bot menu set: ${data.commands.map((c) => "/" + c).join(" ")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get("/admin/signals"), api.get("/signals/plan")]);
      setRows(r.data);
      setPlan(p.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [prov, setProv] = useState(null);
  const [savingProv, setSavingProv] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [grantForm, setGrantForm] = useState({ user_email: "", months: 1, notify: true });
  const [granting, setGranting] = useState(false);
  useEffect(() => { api.get("/admin/signals/provision").then((r) => setProv(r.data)).catch(() => {}); }, []);

  const saveProv = async (patch) => {
    const next = { ...prov, ...patch };
    setProv(next);
    setSavingProv(true);
    try {
      await api.put("/admin/signals/provision", next);
      toast.success("Signals settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingProv(false); }
  };

  const grant = async () => {
    if (!grantForm.user_email.trim()) { toast.error("Enter the user's email"); return; }
    setGranting(true);
    try {
      const { data } = await api.post("/admin/signals/grant", { ...grantForm, months: Number(grantForm.months) });
      toast.success(`Granted ${data.months} month(s)${data.notified ? " · user notified" : ""} — active until ${data.current_period_end.slice(0, 10)}`);
      setShowGrant(false);
      setGrantForm({ user_email: "", months: 1, notify: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to grant"); }
    finally { setGranting(false); }
  };

  const removePost = async (s) => {
    if (!confirm(`Delete "${s.title}"?`)) return;
    try {
      await api.delete(`/admin/signals/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleArchive = async (s) => {
    const archiving = s.status !== "archived";
    try {
      await api.post(`/admin/signals/${s.id}/archive`, { archived: archiving });
      toast.success(archiving ? "Archived — hidden from the feed (history kept)" : "Restored — visible in the feed again");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const archiveOld = async () => {
    if (!confirm("Archive live signals beyond the newest 100? They stay in the system (with TP/SL history) but are hidden from the feed.")) return;
    try {
      const { data } = await api.post("/admin/signals/archive-old");
      toast.success(`Archived ${data.archived} old signal${data.archived === 1 ? "" : "s"} (kept newest ${data.kept})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setOutcome = async (s, outcome) => {
    try {
      const { data } = await api.post(`/admin/signals/${s.id}/outcome`, { outcome: outcome || null });
      if (!outcome) toast.success("Outcome cleared");
      else if (data?.telegram?.sent) toast.success("Outcome posted to Telegram");
      else if (data?.telegram && !data.telegram.sent) toast.warning(`Saved, but Telegram not posted: ${data.telegram.reason || "check setup"}`);
      else toast.success("Outcome saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const getJoinLink = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.get("/admin/telegram/join-link");
      try { await navigator.clipboard?.writeText(data.link); } catch { /* ignore */ }
      toast.success("Member join link copied — permanent, auto-approves active subscribers only");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };
  const regenJoinLink = async () => {
    if (!confirm("Regenerate the members join link? The old link stops working immediately; active subscribers get the new one automatically.")) return;
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/join-link/regenerate");
      try { await navigator.clipboard?.writeText(data.link); } catch { /* ignore */ }
      toast.success("New join link generated & copied");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setTgBusy(false); }
  };

  const savePlan = async (e) => {
    e.preventDefault();
    setSavingPlan(true);
    try {
      await api.put("/admin/signals/plan", {
        price: Number(plan.price),
        currency: plan.currency,
        description: plan.description || "",
        telegram_channel_url: plan.telegram_channel_url || null,
        period_days: Number(plan.period_days) || 30,
      });
      toast.success("Plan saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingPlan(false); }
  };

  return (
    <div data-testid="admin-signals">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Signals</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Signal desk</h1>
          <p className="mt-2 text-sm text-emerald-100/60">{rows.length} post{rows.length === 1 ? "" : "s"}.</p>
        </div>
        <div className="flex gap-2">
          {["posts", "plan", "telegram"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              data-testid={`admin-signals-tab-${t}`}
              className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                tab === t ? "bg-emerald-500/15 text-lime-200" : "text-emerald-100/60 hover:text-white"
              }`}>
              {t}
            </button>
          ))}
          {tab === "posts" && (
            <>
              <button onClick={archiveOld} data-testid="admin-signals-archive-old" className="btn-edge-ghost text-sm" title="Archive live signals beyond the newest 100">
                <Archive size={14} /> Archive old
              </button>
              <button onClick={() => setEditing({ ...empty })} data-testid="admin-signal-new" className="btn-edge text-sm">
                <Plus size={14} /> New signal
              </button>
            </>
          )}
        </div>
      </div>

      {tab === "posts" && (
        <div className="card-edge overflow-hidden">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-emerald-500/10 text-xs uppercase tracking-widest text-emerald-100/50">
              <tr>
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Symbol</th>
                <th className="px-5 py-3 font-medium">Direction</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Outcome</th>
                <th className="px-5 py-3 font-medium">Published</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">No signals yet — start the feed with your first post.</td></tr>}
              {rows.slice((page - 1) * 20, page * 20).map((s) => (
                <tr key={s.id} className="border-b border-emerald-500/8 last:border-0 hover:bg-emerald-500/[0.03]">
                  <td className="px-5 py-3.5 text-white">{s.title}</td>
                  <td className="px-5 py-3.5 text-emerald-100/70">{s.symbol || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`chip ${s.direction === "SELL" ? "chip-amber" : s.direction === "HOLD" ? "chip-neutral" : "chip-emerald"}`}>{s.direction}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`chip ${s.status === "published" ? "chip-emerald" : s.status === "archived" ? "chip-amber" : "chip-neutral"}`}>{s.status}</span>
                    {s.is_example && <span className="chip chip-amber ml-1" data-testid={`admin-signal-example-${s.id}`}>example</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    <select value={s.outcome || ""} onChange={(e) => setOutcome(s, e.target.value)} data-testid={`admin-signal-outcome-${s.id}`}
                      className="rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1.5 text-xs text-white">
                      <option value="">— none —</option>
                      <option value="tp">✅ TP hit</option>
                      <option value="sl">❌ SL hit</option>
                      <option value="be">⚪ Breakeven</option>
                      <option value="cancelled">🚫 Cancelled</option>
                    </select>
                  </td>
                  <td className="px-5 py-3.5 font-mono-edge text-[11px] text-emerald-100/50">{new Date(s.published_at).toLocaleString()}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditing({ ...s, tags: s.tags || [] })} data-testid={`admin-signal-edit-${s.id}`}
                        className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50 hover:text-white">
                        <Edit3 size={14} />
                      </button>
                      {!s.is_example && (
                        <button onClick={() => toggleArchive(s)} data-testid={`admin-signal-archive-${s.id}`}
                          title={s.status === "archived" ? "Restore to feed" : "Archive (hide from feed, keep history)"}
                          className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-amber-400/50 hover:text-amber-300">
                          {s.status === "archived" ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                        </button>
                      )}
                      <button onClick={() => removePost(s)} data-testid={`admin-signal-delete-${s.id}`}
                        className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-red-500/40 hover:text-red-300">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pages={Math.max(1, Math.ceil(rows.length / 20))} total={rows.length} onPage={setPage} testId="admin-signals-pager" />
        </div>
      )}

      {tab === "plan" && plan && (
        <div className="grid gap-6">
          <div className="card-moss p-6 lg:p-8" data-testid="signals-provision">
            <div className="mb-4 flex items-center gap-2"><Power size={16} className="text-lime-300" /><p className="overline">Signals provision</p></div>
            <label className="flex items-center justify-between gap-4 border-b border-emerald-500/10 py-3">
              <span className="text-sm text-emerald-100/85">Signals enabled<br /><span className="text-[11px] text-emerald-100/50">When off, the public Signals page shows “Coming soon”, its nav link is hidden, and new subscribe/trial are blocked. Existing subscribers keep access.</span></span>
              <input type="checkbox" checked={!!prov?.enabled} onChange={(e) => saveProv({ enabled: e.target.checked })} disabled={savingProv || !prov} data-testid="prov-enabled" className="h-5 w-5 accent-lime-400" />
            </label>
            <label className="flex items-center justify-between gap-4 border-b border-emerald-500/10 py-3">
              <span className="text-sm text-emerald-100/85">Free trial enabled<br /><span className="text-[11px] text-emerald-100/50">One free trial per person. Re-registering with the same email can’t reset it.</span></span>
              <input type="checkbox" checked={!!prov?.trial_enabled} onChange={(e) => saveProv({ trial_enabled: e.target.checked })} disabled={savingProv || !prov} data-testid="prov-trial-enabled" className="h-5 w-5 accent-lime-400" />
            </label>
            <div className="flex items-center justify-between gap-4 py-3">
              <span className="text-sm text-emerald-100/85">Trial length (days)</span>
              <input type="number" min={1} max={90} value={prov?.trial_days ?? 7} onChange={(e) => setProv({ ...prov, trial_days: Number(e.target.value) })} onBlur={(e) => saveProv({ trial_days: Math.max(1, Math.min(90, Number(e.target.value) || 7)) })} disabled={!prov} data-testid="prov-trial-days" className="w-24 rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-lime-400/60 focus:outline-none" />
            </div>
          </div>

          <div className="card-moss p-6 lg:p-8">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><Gift size={16} className="text-lime-300" /><p className="overline">Grant signals to a user</p></div>
              <button onClick={() => setShowGrant(true)} data-testid="admin-grant-signals-open" className="btn-edge text-sm"><Gift size={14} /> Grant access</button>
            </div>
            <p className="text-sm text-emerald-100/55">Give any registered user N months of signals access for free (comp, partner, support). Extends their current access if they already have some.</p>
          </div>

          <form onSubmit={savePlan} className="card-moss grid gap-4 p-6 lg:p-8">
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Price" v={plan.price} on={(v) => setPlan({ ...plan, price: v })} type="number" testId="plan-price" />
              <Field label="Currency" v={plan.currency} on={(v) => setPlan({ ...plan, currency: v })} testId="plan-currency" />
              <Field label="Period (days)" v={plan.period_days} on={(v) => setPlan({ ...plan, period_days: v })} type="number" testId="plan-period" />
            </div>
            <Field label="Description" v={plan.description} on={(v) => setPlan({ ...plan, description: v })} textarea testId="plan-desc" />
            <Field label="Telegram channel URL (public)" v={plan.telegram_channel_url} on={(v) => setPlan({ ...plan, telegram_channel_url: v })} testId="plan-tg" />
            <button disabled={savingPlan} data-testid="plan-save" className="btn-edge justify-center">
              {savingPlan ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save plan
            </button>
          </form>
        </div>
      )}

      {tab === "telegram" && (
        <div className="card-moss p-6 lg:p-8" data-testid="admin-telegram">
          <p className="overline mb-2">Telegram automation</p>
          <p className="mb-3 max-w-2xl text-sm text-emerald-100/60">Active subscribers get a <b>permanent join-request link</b> at checkout. Every join is auto-approved <b>only</b> for a linked account with an active subscription, so a shared or leaked link can't let non-paying users in. An hourly job removes members the moment their subscription lapses — and they can rejoin instantly on renewal with the same link.</p>
          <div className="mb-5 max-w-2xl rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-4 text-xs text-emerald-100/70" data-testid="tg-setup-help">
            <p className="mb-1 font-semibold text-lime-300">Where to get these (under Admin → API keys):</p>
            <p>1. <b>Bot token</b> — message <b>@BotFather</b> on Telegram → <code>/newbot</code>, copy the token.</p>
            <p>2. Add that bot to your private group <b>as an admin</b> (with "invite users" + "post messages" rights).</p>
            <p>3. <b>Channel id</b> — the group's numeric id like <code>-1001234567890</code> (or a public <code>@username</code>). Get it by forwarding any group message to <b>@userinfobot</b>. <span className="text-amber-300">Do NOT paste the t.me invite link.</span></p>
          </div>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { k: "Bot token", ok: tg?.bot_token },
              { k: "Channel id", ok: tg?.channel_id },
              { k: "Public URL", ok: !!tg?.public_backend_url },
              { k: "Members tracked", v: tg?.captured_members ?? 0 },
            ].map((x) => (
              <div key={x.k} className="rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 p-4">
                <p className="overline">{x.k}</p>
                <p className="mt-1 font-editorial text-2xl text-white">{"ok" in x ? (x.ok ? "Ready" : "—") : x.v}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={testTg} disabled={tgBusy} data-testid="tg-test" className="btn-edge-ghost text-sm">Test connection &amp; send test message</button>
            <button onClick={detectChats} disabled={tgBusy} data-testid="tg-detect" className="btn-edge text-sm">Detect group id</button>
            <button onClick={setupMenu} disabled={tgBusy} data-testid="tg-setup-menu" className="btn-edge-ghost text-sm">Set up bot menu</button>
            <button onClick={getInvite} disabled={tgBusy} data-testid="tg-invite" className="btn-edge-ghost text-sm">Get admin invite link</button>
            <button onClick={getJoinLink} disabled={tgBusy} data-testid="tg-join-link" className="btn-edge-ghost text-sm">Copy member join link</button>
            <button onClick={regenJoinLink} disabled={tgBusy} data-testid="tg-join-regen" className="btn-edge-ghost text-sm">Regenerate join link</button>
            <button onClick={setupWebhook} disabled={tgBusy} data-testid="tg-setup-webhook" className="btn-edge-ghost text-sm">{tgBusy ? <Loader2 size={14} className="animate-spin" /> : null} Register webhook</button>
            <button onClick={runSweep} disabled={tgBusy} data-testid="tg-run-sweep" className="btn-edge-ghost text-sm">Run expiry sweep now</button>
            <button onClick={runReminders} disabled={tgBusy} data-testid="tg-run-reminders" className="btn-edge-ghost text-sm">Send renewal reminders now</button>
          </div>
          {detected && detected.chats?.length > 0 && (
            <div className="mt-5 rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-4" data-testid="tg-detected">
              <p className="overline mb-2">Detected chats — click to save as channel id</p>
              <div className="grid gap-2">
                {detected.chats.map((c) => (
                  <button key={c.id} onClick={() => saveChannelId(c.id)} data-testid={`tg-chat-${c.id}`}
                    className="flex items-center justify-between rounded-lg border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 text-left text-sm text-white hover:border-lime-400/50">
                    <span>{c.title || "(untitled)"} <span className="text-[11px] text-emerald-100/50">· {c.type}</span></span>
                    <span className="font-mono-edge text-xs text-lime-300">{c.id}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {editing !== null && (
        <SignalModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {showGrant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="grant-signals-modal" onClick={() => setShowGrant(false)}>
          <div className="w-full max-w-md card-moss p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center gap-2"><Gift size={16} className="text-lime-300" /><p className="overline">Grant signals access</p></div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">User email</label>
            <input value={grantForm.user_email} onChange={(e) => setGrantForm({ ...grantForm, user_email: e.target.value })} data-testid="grant-email" placeholder="user@example.com"
              className="mb-3 w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-lime-400/60 focus:outline-none" />
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Months</label>
            <input type="number" min={1} max={36} value={grantForm.months} onChange={(e) => setGrantForm({ ...grantForm, months: e.target.value })} data-testid="grant-months"
              className="mb-3 w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-lime-400/60 focus:outline-none" />
            <label className="mb-5 flex cursor-pointer items-center gap-2 text-xs text-emerald-100/70">
              <input type="checkbox" checked={grantForm.notify} onChange={(e) => setGrantForm({ ...grantForm, notify: e.target.checked })} data-testid="grant-notify" className="accent-lime-400" />
              Notify the user (email + in-app + Telegram if linked)
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowGrant(false)} className="btn-edge-ghost text-sm">Cancel</button>
              <button onClick={grant} disabled={granting} data-testid="grant-submit" className="btn-edge text-sm">{granting ? "Granting…" : "Grant access"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SignalModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;

  const addTag = () => {
    const v = tagInput.trim();
    if (!v) return;
    setForm({ ...form, tags: [...(form.tags || []), v] });
    setTagInput("");
  };
  const removeTag = (i) => setForm({ ...form, tags: form.tags.filter((_, ix) => ix !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        body: form.body || "",
        direction: form.direction,
        symbol: form.symbol || "",
        entry: form.entry || null,
        sl: form.sl || null,
        tp: form.tp || null,
        tags: form.tags || [],
        status: form.status,
        is_example: !!form.is_example,
      };
      if (payload.is_example) {
        if (isNew) await api.post("/admin/signals", payload);
        else await api.put(`/admin/signals/${item.id}`, payload);
        toast.success(payload.status === "published" ? "Example saved — visible on the Signals page" : "Example saved as draft");
      } else if (isNew) {
        const { data } = await api.post("/admin/signals", payload);
        if (payload.status !== "published") toast.success("Signal saved as draft");
        else if (data?.telegram?.sent) toast.success("Signal published & sent to Telegram");
        else toast.warning(`Published, but Telegram not sent: ${data?.telegram?.reason || "check Telegram setup"}`);
      } else {
        const { data } = await api.put(`/admin/signals/${item.id}`, payload);
        if (payload.status === "published" && data?.telegram && !data.telegram.sent) toast.warning(`Updated, but Telegram not sent: ${data.telegram.reason}`);
        else if (payload.status === "published" && data?.telegram?.sent) toast.success("Signal updated & sent to Telegram");
        else toast.success("Signal updated");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card-moss my-8 w-full max-w-xl">
        <div className="flex items-center justify-between border-b border-emerald-500/12 p-5">
          <div>
            <p className="overline">{isNew ? "New" : "Edit"} signal</p>
            <h3 className="mt-1 font-editorial text-xl text-white">{isNew ? "Publish setup" : form.title}</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={save} className="grid gap-4 p-5">
          <Field label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} required testId="signal-title" />
          <Field label="Body" v={form.body} on={(v) => setForm({ ...form, body: v })} textarea testId="signal-body" />
          <div className="grid grid-cols-3 gap-3">
            <SelectField label="Direction" v={form.direction} on={(v) => setForm({ ...form, direction: v })} options={[{v:"BUY",l:"BUY"},{v:"SELL",l:"SELL"},{v:"HOLD",l:"HOLD"}]} testId="signal-direction" />
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-widest text-emerald-100/50">Symbol</label>
              <input list="signal-symbols" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} data-testid="signal-symbol"
                placeholder="Type or pick e.g. XAUUSD" autoComplete="off"
                className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-lime-400/60 focus:outline-none" />
              <datalist id="signal-symbols">
                {["EURUSD","GBPUSD","USDJPY","USDCHF","USDCAD","AUDUSD","NZDUSD","EURGBP","EURJPY","GBPJPY","AUDJPY","EURAUD","EURCHF","EURCAD","GBPCHF","GBPAUD","CADJPY","CHFJPY","NZDJPY","AUDNZD","AUDCAD","USDSGD","USDHKD","USDMXN","USDZAR","USDTRY","USDSEK","USDNOK","EURNZD","GBPCAD","GBPNZD","XAUUSD","XAGUSD","XPTUSD","XPDUSD","USOIL","UKOIL","NATGAS","US30","US500","NAS100","US2000","GER40","UK100","FRA40","EU50","JP225","HK50","AUS200","BTCUSD","ETHUSD","XRPUSD","SOLUSD","ADAUSD","DOGEUSD","BNBUSD","LTCUSD","DOTUSD","AVAXUSD","LINKUSD","MATICUSD","TRXUSD","AAPL","MSFT","AMZN","GOOGL","META","TSLA","NVDA","NFLX","AMD","INTC","JPM","V","DIS","BA","KO","PYPL","BABA","UBER"].map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
            <SelectField label="Status" v={form.status} on={(v) => setForm({ ...form, status: v })} options={[{v:"published",l:"Published"},{v:"draft",l:"Draft"}]} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Entry" v={form.entry} on={(v) => setForm({ ...form, entry: v })} />
            <Field label="SL" v={form.sl} on={(v) => setForm({ ...form, sl: v })} />
            <Field label="TP" v={form.tp} on={(v) => setForm({ ...form, tp: v })} />
          </div>
          <label className="flex items-start gap-2 text-sm text-emerald-100/80">
            <input type="checkbox" checked={!!form.is_example} onChange={(e) => setForm({ ...form, is_example: e.target.checked })} data-testid="signal-is-example" className="mt-0.5 h-4 w-4 accent-lime-400" />
            <span>Example signal — shown on the public Signals page as a sample. <span className="text-emerald-100/50">Not broadcast to subscribers or Telegram, and not shown in dashboards.</span></span>
          </label>
          <div>
            <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Tags</label>
            <div className="grid gap-2">
              {(form.tags || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white">
                  {t}
                  <button type="button" onClick={() => removeTag(i)} className="text-emerald-100/50 hover:text-red-300"><X size={12} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag and Enter" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  className="flex-1 rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
                <button type="button" onClick={addTag} className="btn-edge-ghost text-xs">Add</button>
              </div>
            </div>
          </div>
          <button disabled={saving} data-testid="signal-save" className="btn-edge justify-center">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, v, on, textarea, type = "text", required, placeholder, testId }) {
  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">{label}</label>
      {textarea ? (
        <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={4} data-testid={testId}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" placeholder={placeholder} />
      ) : (
        <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} required={required} data-testid={testId}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" placeholder={placeholder} />
      )}
    </div>
  );
}
function SelectField({ label, v, on, options, testId }) {
  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">{label}</label>
      <select value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId}
        className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2.5 text-sm text-white">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
